import React, { useState, useEffect, useCallback } from 'react';
import { useQC } from './QCContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Users,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Star,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';

// Class colors for badges
const classColors = {
  'Crew Leader': 'bg-purple-500 text-white',
  'A Mechanic': 'bg-blue-500 text-white',
  'B Mechanic': 'bg-green-500 text-white',
  'Helper': 'bg-orange-500 text-white',
};

// Availability options
const AVAILABILITY_OPTIONS = [
  { value: "AO", label: "AO" },
  { value: "FISK", label: "FISK" },
  { value: "NWL", label: "NWL" },
  { value: "WORK_ALL", label: "WORK ALL" },
];

export const QCOvertimeList = ({ currentUserId }) => {
  const { api, user } = useQC();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [selectedClass, setSelectedClass] = useState('all');
  const [updatingAvailability, setUpdatingAvailability] = useState(false);

  const fetchOvertimeList = useCallback(async () => {
    try {
      // Use GETGRIDGATE's bid-order endpoint which returns ranked users
      const response = await api.get('/canvass/bid-order');
      // Handle both array and object response formats
      const responseData = response.data;
      const users = Array.isArray(responseData) ? responseData : (responseData?.users || []);
      
      // Transform to QC format
      const overtimeList = users.map((u, idx) => ({
        id: u.user_id,
        name: u.name,
        email: u.email,
        employee_class: u.employee_class || 'Helper',
        overtime_hours: u.overtime_hours || 0,
        projected_hours: u.overtime_hours || 0,
        ranking: idx + 1,
        rank_in_class: idx + 1,
        status: 'available',
        availability: 'AO',
        availability_label: 'AO',
        is_current_user: u.user_id === (currentUserId || user?.id),
        pending_jobs_count: 0,
        last_increase: 0,
      }));
      
      // Find current user stats
      const currentUserData = overtimeList.find(u => u.is_current_user) || {
        overtime_hours: 0,
        projected_hours: 0,
        ranking: overtimeList.length + 1,
        employee_class: user?.employee_class || 'Helper',
        availability: 'AO',
        pending_jobs: [],
        last_increase: 0,
        current_overtime_hours: 0,
        projected_overtime_hours: 0,
      };
      
      setData({
        overtime_list: overtimeList,
        current_user: {
          ...currentUserData,
          current_overtime_hours: currentUserData.overtime_hours,
          projected_overtime_hours: currentUserData.projected_hours,
        },
      });
    } catch (error) {
      console.error('Error fetching overtime list:', error);
      // Fallback with empty data
      setData({
        overtime_list: [],
        current_user: {
          overtime_hours: 0,
          projected_hours: 0,
          current_overtime_hours: 0,
          projected_overtime_hours: 0,
          ranking: 1,
          employee_class: 'Helper',
          availability: 'AO',
          pending_jobs: [],
          last_increase: 0,
        },
      });
    } finally {
      setLoading(false);
    }
  }, [api, currentUserId, user]);

  useEffect(() => {
    fetchOvertimeList();
    const interval = setInterval(fetchOvertimeList, 30000);
    return () => clearInterval(interval);
  }, [fetchOvertimeList]);

  const updateMyAvailability = async (value) => {
    setUpdatingAvailability(true);
    try {
      // Update via GETGRIDGATE API
      await api.patch('/my/profile', { availability: value });
      toast.success(`Availability updated to: ${value}`);
      fetchOvertimeList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update availability');
    } finally {
      setUpdatingAvailability(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { overtime_list, current_user } = data;
  
  const filteredList = selectedClass === 'all' 
    ? overtime_list 
    : overtime_list.filter(emp => emp.employee_class === selectedClass);

  const classes = ['all', ...new Set(overtime_list.map(emp => emp.employee_class))];
  const projectedDiff = (current_user.projected_overtime_hours || 0) - (current_user.current_overtime_hours || 0);

  return (
    <Card className="w-full" data-testid="qc-overtime-list">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Overtime List
            </CardTitle>
            <CardDescription>
              {overtime_list.length} employees • Ranked by lowest overtime
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchOvertimeList} className="gap-1">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Current User Stats */}
        <div className="bg-primary/10 rounded-lg p-4 border-2 border-primary/20">
          <div className="flex items-center gap-2 mb-2">
            <Star className="h-4 w-4 text-primary" />
            <span className="font-semibold">Your Stats</span>
            <Badge variant="outline">{current_user.employee_class}</Badge>
          </div>
          
          <div className="grid grid-cols-3 gap-3 mt-3 p-3 bg-background/50 rounded-lg border">
            <div className="text-center">
              <p className="text-xs text-muted-foreground font-medium">Actual Hours</p>
              <p className="text-2xl font-bold">{(current_user.current_overtime_hours || 0).toFixed(1)}</p>
              <p className="text-xs text-muted-foreground">Current OT</p>
            </div>
            <div className="text-center border-x border-border">
              <p className="text-xs text-blue-600 font-medium">Projected</p>
              <p className="text-2xl font-bold text-blue-600">
                {(current_user.projected_overtime_hours || 0).toFixed(1)}
              </p>
              <p className="text-xs text-muted-foreground">
                {projectedDiff > 0 ? `+${projectedDiff.toFixed(1)} pending` : 'No pending'}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground font-medium">Your Rank</p>
              <p className="text-2xl font-bold">#{current_user.ranking || 'N/A'}</p>
              <p className="text-xs text-muted-foreground">Lower is better</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Your Availability</p>
              <Select
                value={current_user.availability || "AO"}
                onValueChange={updateMyAvailability}
                disabled={updatingAvailability}
              >
                <SelectTrigger className="w-[140px] h-8">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {AVAILABILITY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Class Filter */}
        <div className="flex gap-2 flex-wrap">
          {classes.map(cls => (
            <Button
              key={cls}
              variant={selectedClass === cls ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedClass(cls)}
              className="text-xs"
            >
              {cls === 'all' ? 'All Classes' : cls}
            </Button>
          ))}
        </div>

        {/* Toggle List */}
        <Button variant="ghost" className="w-full" onClick={() => setExpanded(!expanded)}>
          {expanded ? (
            <>
              <ChevronUp className="h-4 w-4 mr-2" />
              Hide Full List
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4 mr-2" />
              Show Full Overtime List ({filteredList.length} employees)
            </>
          )}
        </Button>

        {/* Full List */}
        {expanded && (
          <ScrollArea className="h-[400px] rounded-md border">
            <div className="p-4 space-y-2">
              {filteredList.map((emp, index) => (
                <div 
                  key={emp.id}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                    emp.is_current_user 
                      ? 'bg-primary/10 border-primary/30 ring-2 ring-primary/20' 
                      : 'bg-card hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${emp.is_current_user ? 'text-primary' : ''}`}>
                          {emp.name}
                          {emp.is_current_user && <span className="text-xs ml-1">(You)</span>}
                        </span>
                      </div>
                      <Badge className={`text-xs ${classColors[emp.employee_class] || 'bg-gray-500'}`}>
                        {emp.employee_class}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <span className="font-bold">{emp.overtime_hours.toFixed(1)}h</span>
                    <p className="text-xs text-muted-foreground">overtime</p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}

        {/* Quick Stats */}
        {!expanded && (
          <div className="grid grid-cols-4 gap-2 text-center">
            {['Crew Leader', 'A Mechanic', 'B Mechanic', 'Helper'].map(cls => {
              const count = overtime_list.filter(e => e.employee_class === cls).length;
              return (
                <div key={cls} className="p-2 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground truncate">{cls}</p>
                  <p className="font-bold">{count}</p>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QCOvertimeList;
